import { useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import type * as echarts from 'echarts';
import moment from 'moment';
import { getCssVariable } from '../../../utils/utils';
import {
  SentimentDataPoint,
  SentimentDays,
} from '../../../types/sentiment.type';

export interface SentimentBarChartProps {
  data: SentimentDataPoint[];
  period?: SentimentDays;
}

const GLOBAL_FONT = "'Arial', 'Helvetica', 'Sans-serif'";

const SentimentBarChart = ({ data, period }: SentimentBarChartProps) => {
  const chartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeTab = period === '1' ? '24h' : period;

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance().resize();
    });
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const barAriaDescription = `Media buzz over the selected ${activeTab ?? ''} period`;

  const TARGET_TICK_COUNT = 5;
  const tickInterval =
    data.length > TARGET_TICK_COUNT
      ? Math.floor(data.length / TARGET_TICK_COUNT) - 1
      : 0;

  const xCategories = data.map((d) =>
    activeTab === '24h'
      ? moment(d.ts).format('HH:mm')
      : moment(d.ts).format('DD MMM'),
  );

  const mediaBuzzSeries: echarts.SeriesOption[] = [
    {
      type: 'bar',
      data: data.map((d) => Number(d.buzz)),
      barWidth: 3,
      barGap: 2,
      itemStyle: {
        color: getCssVariable('--chart-primary'),
        borderRadius: 0,
      },
    },
  ];

  const barOption: echarts.EChartsOption = {
    grid: {
      top: 0,
      bottom: 50,
    },
    aria: {
      show: true,
      label: {
        description: barAriaDescription,
      },
    } as echarts.AriaComponentOption,
    xAxis: {
      type: 'category',
      data: xCategories,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        fontFamily: GLOBAL_FONT,
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: 14,
        rotate: 45,
        color: getCssVariable('--text-secondary'),
        hideOverlap: true,
        interval: tickInterval,
      },
    },
    yAxis: {
      type: 'value',
      show: false,
    },
    series: mediaBuzzSeries,
  };

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <ReactECharts
        ref={chartRef}
        option={barOption}
        style={{
          width: '100%',
          height: '200px',
        }}
      />
    </div>
  );
};

export default SentimentBarChart;
